import type { ID } from "@/lib/catalog/types";
import { replaceAllAttributeGroups, type AttributeGroupRecord } from "@/lib/catalog/attributeGroupsStore";
import { replaceAllCatalogAttributes, type CatalogAttributeRecord } from "@/lib/catalog/catalogAttributesStore";
import { replaceAllCategories, type CategoryRecord } from "@/lib/catalog/categoriesStore";
import { replaceAllLinks, type CategoryAttributeLink } from "@/lib/catalog/categoryAttributeLinksStore";

const BOOT_KEY = "inventory.catalog.bootstrapped.v1";

function id(prefix: string, n: number): ID {
  return `${prefix}_${n}`;
}

/** Initial dataset mirroring the former mock (stable IDs for existing localStorage products). */
export function seedCatalogIfEmpty() {
  if (localStorage.getItem(BOOT_KEY)) return;

  const now = new Date().toISOString();

  const categories: CategoryRecord[] = [
    { id: id("cat", 1), name: "Cars", parentId: null, createdAt: now, updatedAt: now },
    { id: id("cat", 2), name: "Car Parts", parentId: null, createdAt: now, updatedAt: now },
  ];

  const groups: AttributeGroupRecord[] = [
    {
      id: id("grp", 1),
      name: "Basic Specs",
      slug: "basic-specs",
      description: "Core vehicle identification, assembly, engine, and appearance fields for this listing.",
      sortOrder: 1,
      createdAt: now,
    },
    {
      id: id("grp", 2),
      name: "Documents",
      slug: "documents",
      description: "Registration, inspection, and other paperwork buyers may need.",
      sortOrder: 2,
      createdAt: now,
    },
    {
      id: id("grp", 3),
      name: "Features",
      slug: "features",
      description: "Safety, comfort, and technology options equipped on this vehicle.",
      sortOrder: 3,
      createdAt: now,
    },
    {
      id: id("grp", 4),
      name: "Part Details",
      slug: "part-details",
      description: "Brand, condition, and compatibility notes for spare parts and accessories.",
      sortOrder: 1,
      createdAt: now,
    },
  ];

  const attributes: CatalogAttributeRecord[] = [
    {
      id: id("attr", 1),
      attributeGroupId: id("grp", 1),
      code: "make",
      name: "Make / Brand",
      type: "text",
      isRequired: true,
      isListing: true,
      isFilter: true,
      sortOrder: 1,
      createdAt: now,
    },
    {
      id: id("attr", 2),
      attributeGroupId: id("grp", 1),
      code: "model",
      name: "Model",
      type: "text",
      isRequired: true,
      isListing: true,
      isFilter: true,
      sortOrder: 2,
      createdAt: now,
    },
    {
      id: id("attr", 3),
      attributeGroupId: id("grp", 1),
      code: "year",
      name: "Year",
      type: "number",
      isRequired: true,
      isListing: false,
      isFilter: true,
      sortOrder: 3,
      createdAt: now,
    },
    {
      id: id("attr", 4),
      attributeGroupId: id("grp", 1),
      code: "assembly",
      name: "Assembly",
      type: "select",
      isRequired: true,
      isListing: true,
      isFilter: true,
      sortOrder: 4,
      options: [
        { id: id("opt", 1), value: "local", label: "Local" },
        { id: id("opt", 2), value: "imported", label: "Imported" },
      ],
      createdAt: now,
    },
    {
      id: id("attr", 5),
      attributeGroupId: id("grp", 1),
      code: "engine_cc",
      name: "Engine Capacity (cc)",
      type: "number",
      isListing: true,
      isFilter: true,
      sortOrder: 5,
      createdAt: now,
    },
    {
      id: id("attr", 6),
      attributeGroupId: id("grp", 1),
      code: "color",
      name: "Color",
      type: "text",
      isListing: true,
      isFilter: true,
      sortOrder: 6,
      createdAt: now,
    },
    {
      id: id("attr", 7),
      attributeGroupId: id("grp", 2),
      code: "registration_docs",
      name: "Registration Documents (PDF)",
      type: "file",
      accept: "application/pdf",
      maxSizeMb: 15,
      sortOrder: 1,
      createdAt: now,
    },
    {
      id: id("attr", 8),
      attributeGroupId: id("grp", 2),
      code: "gallery",
      name: "Gallery Images",
      type: "images",
      accept: "image/*",
      maxFiles: 10,
      maxSizeMb: 10,
      sortOrder: 2,
      createdAt: now,
    },
    {
      id: id("attr", 9),
      attributeGroupId: id("grp", 3),
      code: "features",
      name: "Features",
      type: "multiselect",
      isListing: false,
      isFilter: true,
      sortOrder: 1,
      options: [
        { id: id("feat", 1), value: "abs", label: "ABS" },
        { id: id("feat", 2), value: "airbags", label: "Airbags" },
        { id: id("feat", 3), value: "rear_camera", label: "Rear Camera" },
        { id: id("feat", 4), value: "alloy_wheels", label: "Alloy Wheels" },
      ],
      createdAt: now,
    },
    {
      id: id("attr", 20),
      attributeGroupId: id("grp", 4),
      code: "brand",
      name: "Brand",
      type: "text",
      isRequired: true,
      sortOrder: 1,
      createdAt: now,
    },
    {
      id: id("attr", 21),
      attributeGroupId: id("grp", 4),
      code: "condition",
      name: "Condition",
      type: "select",
      isRequired: true,
      sortOrder: 2,
      options: [
        { id: id("cond", 1), value: "new", label: "New" },
        { id: id("cond", 2), value: "used", label: "Used" },
      ],
      createdAt: now,
    },
    {
      id: id("attr", 22),
      attributeGroupId: id("grp", 4),
      code: "compatibility",
      name: "Compatibility Notes",
      type: "textarea",
      sortOrder: 3,
      createdAt: now,
    },
    {
      id: id("attr", 23),
      attributeGroupId: id("grp", 4),
      code: "product_image",
      name: "Product Image",
      type: "image",
      accept: "image/*",
      maxSizeMb: 10,
      sortOrder: 4,
      createdAt: now,
    },
  ];

  const links: CategoryAttributeLink[] = [];
  let order = 0;
  const carAttrs = [1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => id("attr", n));
  for (const aid of carAttrs) {
    links.push({ id: crypto.randomUUID(), categoryId: id("cat", 1), attributeId: aid, sortOrder: order++ });
  }
  order = 0;
  const partAttrs = [20, 21, 22, 23].map((n) => id("attr", n));
  for (const aid of partAttrs) {
    links.push({ id: crypto.randomUUID(), categoryId: id("cat", 2), attributeId: aid, sortOrder: order++ });
  }

  replaceAllCategories(categories);
  replaceAllAttributeGroups(groups);
  replaceAllCatalogAttributes(attributes);
  replaceAllLinks(links);

  localStorage.setItem(BOOT_KEY, "1");
}
